import React, { useEffect, useMemo, useState } from 'react';
import { digitalApi as api } from './api';
import { C } from '../../theme';
import CatalogueTable, { normalizeGlobalPlatform, computeRefreshDiff } from './CatalogueTable';
import { calcFlight, combineTot } from './DigitalPlanningEngine';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useToast } from '../../components/Toast';

// Plan-level Catalogue view (catalogue parity step 3a, 2026-10-04) — a full page inside the plan
// editor (not a route, not a drawer): unsaved flights/mix live in the editor's own React state, so
// this has to be a sibling view rendered by the SAME top-level component, toggled in and out,
// never a navigation away from it. Built on the shared CatalogueTable with scope='plan'.
//
// `catalogue`/`setCatalogue` ARE the plan editor's own live state (identical reference the rest of
// the editor — FlightModal, PlatformMixTable, the solver, Summary/Details — already reads this
// exact array from) — editing here updates it in place so everything else recomputes immediately
// via the editor's existing reactivity, same as the old drawer's "Plan" scope already does. This
// view never touches Client.catalogue_overrides or the global platforms table — every write goes
// through PATCH /plans/{id}/catalogue or POST /plans/{id}/catalogue/(refresh|undo-refresh), which
// only ever merge into THIS plan's own frozen entries.

// Verbatim copies of the main KPI bar's formatters (DigitalPlanningEngine.js) — kept identical so
// the refresh dialog's before/after figures read exactly like the KPI bar itself.
const fe   = n => (!isFinite(n)||isNaN(n)||n===0) ? '—' : '€'+Math.round(n).toLocaleString();
const fmil = n => { if(!n||!isFinite(n)) return '—'; if(n>=1e6) return (n/1e6).toFixed(1)+'M'; if(n>=1000) return Math.round(n/1000)+'K'; return Math.round(n)+''; };
const fp   = n => (!isFinite(n)||isNaN(n)) ? '—' : (n*100).toFixed(1)+'%';

const FIELD_LABELS = {
  base_cpm:'CPM €', cpc:'CPC €', base_discount:'Base Discount', seasonal_cpm:'Seasonal CPM',
  viewability:'Viewability', ctr:'CTR%', vtr:'VTR%', engagement_rate:'Engagement Rate', cvr:'CVR',
  weekly_frq:'Weekly FRQ', monthly_frq:'Monthly FRQ', audience_comp:'Audience Comp.', real_users_coef:'RUC',
  weekly_impression_cap:'Weekly Impression Cap', monthly_impression_cap:'Monthly Impression Cap', active:'Active',
};
const PCT_FIELDS_2DP = new Set(['ctr','cvr']);
const PCT_FIELDS = new Set(['base_discount','viewability','ctr','vtr','engagement_rate','cvr','audience_comp']);
function formatFieldValue(field, value) {
  if (value == null || value === '') return '—';
  if (field === 'base_cpm' || field === 'cpc') return `€${Number(value).toFixed(2)}`;
  if (PCT_FIELDS.has(field)) return `${(value*100).toFixed(PCT_FIELDS_2DP.has(field)?2:0)}%`;
  if (field === 'weekly_frq' || field === 'monthly_frq') return `${value} avg/wk`;
  if (field === 'weekly_impression_cap' || field === 'monthly_impression_cap') return Number(value).toLocaleString();
  if (field === 'active') return value ? 'active' : 'inactive';
  if (field === 'seasonal_cpm') return Array.isArray(value) ? `[${value.map(v=>Number(v).toFixed(2)).join(', ')}]` : '—';
  return String(value);
}

// Mirrors DigitalPlanningEngine.js's own allResults/planTot computation (same calcFlight +
// combineTot calls, same per-flight audience resolution) — reused directly (not reimplemented) so
// a "before/after" KPI preview can never drift from what the plan editor itself would show after
// a real save. `cat` is the (possibly simulated) catalogue array to score flights against.
function planKPI(cat, flights, audiences, universe, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility) {
  const results = (flights||[]).map(f => {
    const aud = f.audienceId && audiences ? audiences.find(a=>a.id===f.audienceId) : null;
    return calcFlight(f, cat, aud?aud.universe:universe, aud||null, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility);
  });
  return combineTot(results);
}

function KpiRow({ label, before, after, fmt }) {
  const changed = before !== after;
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'4px 0', borderBottom:'1px solid #eef4f8' }}>
      <span style={{ color:'#6a8aaa' }}>{label}</span>
      <span>
        <span style={{ color:'#8aa0b8' }}>{fmt(before)}</span>
        <span style={{ margin:'0 6px', color:'#c8d8e8' }}>→</span>
        <span style={{ fontWeight:700, color: changed?'#0090d0':'#1a2a3a' }}>{fmt(after)}</span>
      </span>
    </div>
  );
}

function RefreshDialog({ rows, catalogue, flights, audiences, universe, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility, onCancel, onConfirm, confirming }) {
  const [checked, setChecked] = useState(() => {
    const s = {};
    rows.forEach(r => { s[r.platformDbId+'|'+r.field] = !r.deliberate; });
    return s;
  });
  const tickedRows = rows.filter(r => checked[r.platformDbId+'|'+r.field]);
  const simulatedCatalogue = useMemo(() => {
    if (!tickedRows.length) return catalogue;
    const byDbId = {};
    tickedRows.forEach(r => { byDbId[r.platformDbId] = { ...(byDbId[r.platformDbId]||{}), [r.field]: r.liveValue }; });
    return catalogue.map(p => byDbId[p._db_id] ? { ...p, ...byDbId[p._db_id] } : p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, catalogue]);
  const kpiBefore = useMemo(() => planKPI(catalogue, flights, audiences, universe, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility), [catalogue, flights, audiences, universe, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility]);
  const kpiAfter = useMemo(() => planKPI(simulatedCatalogue, flights, audiences, universe, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility), [simulatedCatalogue, flights, audiences, universe, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onCancel}>
      <div style={{ ...C.modal, width:760, maxHeight:'86vh', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>Refresh from catalogue</h2>
          <button onClick={onCancel} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ overflowY:'auto', flex:1, marginBottom:16 }}>
          {rows.length === 0 ? (
            <div style={C.empty}>Nothing differs from the current catalogue.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ textAlign:'left', color:'#8aa0b8', fontSize:10, textTransform:'uppercase' }}>
                  <th style={{ padding:'4px 6px', width:28 }}/>
                  <th style={{ padding:'4px 6px' }}>Platform</th>
                  <th style={{ padding:'4px 6px' }}>Field</th>
                  <th style={{ padding:'4px 6px' }}>Frozen value</th>
                  <th style={{ padding:'4px 6px' }}>New value</th>
                  <th style={{ padding:'4px 6px' }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const k = r.platformDbId+'|'+r.field;
                  return (
                    <tr key={k} style={{ borderBottom:'1px solid #eef4f8' }}>
                      <td style={{ padding:'6px' }}><input type="checkbox" checked={!!checked[k]} onChange={e=>setChecked(prev=>({...prev,[k]:e.target.checked}))} style={{ accentColor:'#0090d0', cursor:'pointer' }}/></td>
                      <td style={{ padding:'6px', fontWeight:600 }}>{r.platformName}</td>
                      <td style={{ padding:'6px' }}>
                        {FIELD_LABELS[r.field]||r.field}
                        {r.deliberate && <div style={{ fontSize:9, color:'#7030A0', fontWeight:700 }}>Set in this plan</div>}
                      </td>
                      <td style={{ padding:'6px', color:'#a02020' }}>{formatFieldValue(r.field, r.frozenValue)}</td>
                      <td style={{ padding:'6px', fontWeight:600 }}>{formatFieldValue(r.field, r.liveValue)}</td>
                      <td style={{ padding:'6px' }}><span style={C.mutedLabel()}>{r.source==='client'?'Client value':'Global value'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {rows.length > 0 && (
          <div style={{ background:'#f4f8fb', border:'1px solid #dce8f0', borderRadius:8, padding:'10px 14px', marginBottom:16 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#8aa0b8', textTransform:'uppercase', marginBottom:4 }}>Plan KPI — before → after ({tickedRows.length} ticked)</div>
            <KpiRow label="Budget" before={kpiBefore.budget} after={kpiAfter.budget} fmt={fe}/>
            <KpiRow label="Impressions" before={kpiBefore.impressions} after={kpiAfter.impressions} fmt={fmil}/>
            <KpiRow label="vRCH%" before={kpiBefore.reach} after={kpiAfter.reach} fmt={fp}/>
            <KpiRow label="Views" before={kpiBefore.views} after={kpiAfter.views} fmt={fmil}/>
            <KpiRow label="Clicks" before={kpiBefore.clicks} after={kpiAfter.clicks} fmt={fmil}/>
          </div>
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button style={C.btnSecondary} onClick={onCancel}>Cancel</button>
          <button style={C.btnPrimary} disabled={!tickedRows.length||confirming} onClick={()=>onConfirm(tickedRows.map(r=>({platform_db_id:r.platformDbId, field:r.field})))}>
            {confirming ? 'Refreshing…' : `Refresh ${tickedRows.length} field(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlanCatalogueView({
  planId, planName, planStatus, catalogue, setCatalogue, canEdit, hasSites, isSuperAdmin, onBack,
  universe, setUniverse, deliberate, setDeliberate, lastRefresh, setLastRefresh,
  flights, audiences, sitesMap, siteOverlaps, trackersCatalogue, trackerEligibility,
}) {
  const [globalPlatforms, setGlobalPlatforms] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshRows, setRefreshRows] = useState(null); // null = dialog closed
  const [confirming, setConfirming] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    Promise.all([api.getPlatforms(), api.getSites()])
      .then(([p, s]) => { setGlobalPlatforms(p); setSites(s); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const globalByDbId = useMemo(() => Object.fromEntries(globalPlatforms.map(p => [p.id, normalizeGlobalPlatform(p)])), [globalPlatforms]);

  // CatalogueTable's identity column reads `.name`; the engine's own merged catalogue objects use
  // `.label` (the convention every other plan-editor component — FlightModal, Excel export,
  // Summary — already depends on, so it's deliberately NOT renamed here). This is a read-only
  // display alias, not a second source of truth — persist/reset below always key off `_db_id`.
  const platformsForTable = useMemo(() => catalogue.map(p => ({ ...p, name: p.label })), [catalogue]);

  const readOnlyReason = planStatus === 'approved'
    ? 'This plan is approved. Approved plans are read-only — reject it to draft first to make catalogue changes.'
    : null;

  const allDiffRows = useMemo(() => computeRefreshDiff(catalogue, globalByDbId, deliberate), [catalogue, globalByDbId, deliberate]);
  const refreshablePlatformIds = useMemo(() => new Set(allDiffRows.map(r => r.platformDbId)), [allDiffRows]);

  async function persist({ isNew, id, data }) {
    if (isNew) return; // plan scope never creates new platforms
    const updated = await api.updatePlanCatalogue(planId, id, data);
    const { _deliberate_fields, ...entryUpdate } = updated;
    setCatalogue(prev => prev.map(p => p._db_id === id ? { ...p, ...entryUpdate } : p));
    if (_deliberate_fields) setDeliberate(prev => ({ ...prev, [String(id)]: _deliberate_fields }));
  }

  // A single-field "reset to client/global" is exactly a 1-row refresh (accept the live value) —
  // routed through the same refresh endpoint so it gets the same authoritative live-value
  // computation, the same undo snapshot, and the same catalogue_deliberate bookkeeping, rather than
  // being treated as a brand-new deliberate edit (which the plain PATCH route would do).
  async function onResetField(dbId, fieldKey) {
    await applyRefresh([{ platform_db_id: dbId, field: fieldKey }]);
  }

  async function applyRefresh(entries) {
    const res = await api.refreshPlanCatalogue(planId, entries);
    setCatalogue(res.catalogue.map(p => ({ ...p })));
    setDeliberate(res.catalogue_deliberate || {});
    setLastRefresh(res.last_refresh || null);
    return res;
  }

  async function handleConfirmRefresh(entries) {
    setConfirming(true);
    try {
      const res = await applyRefresh(entries);
      setRefreshRows(null);
      if (res.skipped && res.skipped.length) {
        showToast(`Refreshed ${res.updated.length} field(s) — ${res.skipped.length} skipped (see console)`, 'warning');
        // eslint-disable-next-line no-console
        console.warn('Refresh skipped rows:', res.skipped);
      } else {
        showToast(`Refreshed ${res.updated.length} field(s)`, 'success');
      }
    } catch(e) {
      showToast('Refresh failed — try again', 'danger');
    } finally {
      setConfirming(false);
    }
  }

  async function handleUndo() {
    setUndoing(true);
    try {
      const res = await api.undoPlanCatalogueRefresh(planId);
      setCatalogue(res.catalogue.map(p => ({ ...p })));
      setDeliberate(res.catalogue_deliberate || {});
      setLastRefresh(null);
      showToast('Last refresh undone', 'success');
    } catch(e) {
      showToast('Undo failed — try again', 'danger');
    } finally {
      setUndoing(false);
    }
  }

  if (loading) return <div style={C.empty}>Loading…</div>;
  return (
    <div style={C.page}>
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24, flexWrap:'wrap' }}>
        <button onClick={onBack} style={{...C.btnSecondary, display:'inline-flex', alignItems:'center', gap:6}}><ArrowLeft size={14}/>Back to plan</button>
        <div style={{ fontSize:13, fontWeight:700, color:'#6a8aaa' }}>Plan: {planName}</div>
        <label style={{ display:'flex', alignItems:'center', gap:8, marginLeft:8 }}>
          <span style={C.label}>Default Universe</span>
          <input type="number" step={1000} defaultValue={universe} disabled={!canEdit||!!readOnlyReason} title={readOnlyReason||'Fallback universe used when a flight has no audience selected'}
            onBlur={e=>{ const v=+e.target.value; if (Number.isFinite(v)&&v>0) setUniverse(v); else e.target.value=universe; }}
            style={{...C.input, width:120, padding:'5px 8px'}}/>
        </label>
        <div style={{ flex:1 }}/>
        {lastRefresh && (
          <button style={{...C.btnSecondary, display:'inline-flex', alignItems:'center', gap:6}} disabled={!canEdit||!!readOnlyReason||undoing} title={readOnlyReason||`Undo the refresh by ${lastRefresh.user_email} at ${new Date(lastRefresh.at).toLocaleString()}`} onClick={handleUndo}>
            <RotateCcw size={13}/>{undoing?'Undoing…':'Undo last refresh'}
          </button>
        )}
      </div>
      <CatalogueTable
        scope="plan"
        platforms={platformsForTable}
        sites={sites}
        canEdit={canEdit}
        persist={persist}
        onReload={()=>{}}
        showPublisherCols={hasSites||isSuperAdmin}
        globalByDbId={globalByDbId}
        onResetField={onResetField}
        readOnlyReason={readOnlyReason}
        refreshablePlatformIds={refreshablePlatformIds}
        onRefreshRow={(dbId)=>setRefreshRows(computeRefreshDiff(catalogue, globalByDbId, deliberate, dbId))}
        onRefreshAll={()=>setRefreshRows(allDiffRows)}
      />
      {refreshRows && (
        <RefreshDialog
          rows={refreshRows}
          catalogue={catalogue}
          flights={flights} audiences={audiences} universe={universe}
          sitesMap={sitesMap} siteOverlaps={siteOverlaps}
          trackersCatalogue={trackersCatalogue} trackerEligibility={trackerEligibility}
          onCancel={()=>setRefreshRows(null)}
          onConfirm={handleConfirmRefresh}
          confirming={confirming}
        />
      )}
    </div>
  );
}
